import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm, Control, UseFormTrigger, UseFormWatch, UseFormSetValue, UseFormReset, UseFormGetValues, FormState } from 'react-hook-form';
import { useMediaQuery, useTheme } from '@mui/material';
import { paymentService } from '../services/paymentService';
import { FormSection } from '../types/questionnaire.types';
import { StripeCCFieldHandle } from '../components/questionnaires/StripeCCField';
import { validateFieldValue } from '../utils/questionnaireFieldUtils';
import { getCustomIcon } from '../constants/customIcons';

type FormValues = Record<string, any>;

export interface UseQuestionnaireWizardConfig {
  /** The sections of the questionnaire definition */
  sections: FormSection[];
  /** Total number of sections */
  totalSections: number;
  /** Current section index (controlled externally) */
  sectionIndex: number;
  /** Setter for current section index */
  setSectionIndex: (index: number) => void;
  /** The token for the questionnaire (public wizard) or null */
  token?: string | null;
  /** The exit popup id from the questionnaire definition */
  exitPopupId?: string;
  /** Whether the user has started the screening (has token or filled fields) */
  hasStartedScreening?: boolean;
  /** Text variant for exit popup content */
  exitPopupVariant?: 'screening' | 'questionnaire';
  /** Whether this questionnaire is tied to a lead; drives showBasedOnLead sections */
  hasLead?: boolean;
  /**
   * Callback invoked by auto-advance when the last page of a section is completed.
   * The hook calls this with `skipNavigation=true` so the caller can save answers
   * and then show a popup or navigate. The caller should return a Promise<boolean>
   * indicating success.
   */
  onAutoAdvanceSection?: (skipNavigation: boolean) => Promise<boolean | undefined>;
}

export interface UseQuestionnaireWizardReturn {
  // form
  control: Control<FormValues>;
  trigger: UseFormTrigger<FormValues>;
  watch: UseFormWatch<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  reset: UseFormReset<FormValues>;
  getValues: UseFormGetValues<FormValues>;
  formState: FormState<FormValues>;
  formValues: FormValues;

  // state
  saving: boolean;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  openPopupId: string | null;
  setOpenPopupId: React.Dispatch<React.SetStateAction<string | null>>;
  showExitPopup: boolean;
  setShowExitPopup: React.Dispatch<React.SetStateAction<boolean>>;
  hasFilledFields: boolean;
  setHasFilledFields: React.Dispatch<React.SetStateAction<boolean>>;
  paymentFormReady: boolean;
  setPaymentFormReady: React.Dispatch<React.SetStateAction<boolean>>;
  paymentAmount: number;
  setPaymentAmount: React.Dispatch<React.SetStateAction<number>>;
  sectionPageIndex: number;
  setSectionPageIndex: React.Dispatch<React.SetStateAction<number>>;
  scores: Record<string, any>;
  setScores: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  cameFromBackNav: boolean;
  setCameFromBackNav: React.Dispatch<React.SetStateAction<boolean>>;
  navigateAfterPopup: boolean;
  setNavigateAfterPopup: React.Dispatch<React.SetStateAction<boolean>>;
  buttonGroupPopupContext: { fieldName: string; resetOnBack: boolean } | null;
  setButtonGroupPopupContext: React.Dispatch<React.SetStateAction<{ fieldName: string; resetOnBack: boolean } | null>>;
  navigateToPage: number | null;
  setNavigateToPage: React.Dispatch<React.SetStateAction<number | null>>;

  // refs
  scoresRef: React.MutableRefObject<Record<string, any>>;
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  stripeCCFieldRef: React.MutableRefObject<StripeCCFieldHandle | null>;
  autoAdvanceTriggeredRef: React.MutableRefObject<boolean>;
  targetPageIndexRef: React.MutableRefObject<number | null>;
  skipBeforeUnloadRef: React.MutableRefObject<boolean>;

  // computed
  isMobile: boolean;
  currentSection: FormSection | undefined;

  // handlers
  updateScores: (newScores: Record<string, any>) => void;
  isSectionVisible: (section: FormSection | any, currentScores: Record<string, any>) => boolean;
  triggerExitPopup: () => boolean;
  getExitPopupContent: () => { line1: string; line2: string };
  handleOpenPopup: (popupId: string) => void;
  handleClosePopup: () => void;
  scrollToFirstError: () => void;
  loadPaymentAmountIfNeeded: (section: any) => Promise<void>;
  navigateToNextVisibleSection: (updatedScores?: Record<string, any>) => Promise<void>;
  navigateToPrevVisibleSection: () => void;
  getFieldsForCurrentPage: () => any[];
  hasMorePages: () => boolean;
  getPagePopupId: () => string | null;
  findFirstProblematicField: (section: any) => string | null;
  injectCustomIcons: (htmlContent: string, customIcons: string[]) => string;
}

export function useQuestionnaireWizard(config: UseQuestionnaireWizardConfig): UseQuestionnaireWizardReturn {
  const {
    sections,
    totalSections,
    sectionIndex,
    setSectionIndex,
    token,
    exitPopupId,
    hasStartedScreening: hasStartedScreeningProp,
    exitPopupVariant = 'screening',
    hasLead = false,
    onAutoAdvanceSection,
  } = config;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ─── react-hook-form ─────────────────────────────────────────────────────
  const { control, trigger, watch, setValue, reset, getValues, formState } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {},
  });
  const formValues = watch();

  // ─── State ────────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [openPopupId, setOpenPopupId] = useState<string | null>(null);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [hasFilledFields, setHasFilledFields] = useState(false);
  const [paymentFormReady, setPaymentFormReady] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [sectionPageIndex, setSectionPageIndex] = useState(0);
  const [scores, setScores] = useState<Record<string, any>>({});
  const [cameFromBackNav, setCameFromBackNav] = useState(false);
  const [navigateAfterPopup, setNavigateAfterPopup] = useState(false);
  const [buttonGroupPopupContext, setButtonGroupPopupContext] = useState<{
    fieldName: string;
    resetOnBack: boolean;
  } | null>(null);
  const [navigateToPage, setNavigateToPage] = useState<number | null>(null);

  // ─── Refs ─────────────────────────────────────────────────────────────────
  const scoresRef = useRef<Record<string, any>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stripeCCFieldRef = useRef<StripeCCFieldHandle>(null);
  const autoAdvanceTriggeredRef = useRef(false);
  const autoAdvanceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousFilledCountRef = useRef(0);
  const targetPageIndexRef = useRef<number | null>(null);
  const previousSectionRef = useRef<number>(0);
  const skipBeforeUnloadRef = useRef(false);

  // ─── Derived ──────────────────────────────────────────────────────────────
  const currentSection = sections[sectionIndex] as FormSection | undefined;

  // hasStartedScreening - can be overridden by prop, or computed
  const hasStartedScreening = hasStartedScreeningProp !== undefined
    ? hasStartedScreeningProp
    : (!!token || hasFilledFields);

  // ─── Track if user has filled any fields ──────────────────────────────────
  useEffect(() => {
    const hasValues = Object.values(formValues).some((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== undefined && value !== null && value !== '';
    });
    setHasFilledFields(hasValues);
  }, [formValues]);

  // ─── Initialize autoAdvanceTriggeredRef on mount ───────────────────────────
  useEffect(() => {
    autoAdvanceTriggeredRef.current = false;
  }, []);

  // ─── Reset payment form ready state when section changes ──────────────────
  useEffect(() => {
    setPaymentFormReady(false);
  }, [sectionIndex]);

  // ─── Reset page index when section changes ───────────────────────────────
  useEffect(() => {
    if (targetPageIndexRef.current !== null) {
      setSectionPageIndex(targetPageIndexRef.current);
      targetPageIndexRef.current = null;
      setCameFromBackNav(true);
      autoAdvanceTriggeredRef.current = true;
    } else {
      setSectionPageIndex(0);
      autoAdvanceTriggeredRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIndex]);

  // ─── beforeunload effect ────────────────────────────────────────────────
  useEffect(() => {
    if (!hasStartedScreening || !exitPopupId) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (skipBeforeUnloadRef.current) return;
      e.preventDefault();
      e.returnValue = true;
      if (token) {
        const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';
        navigator.sendBeacon(`${baseUrl}/public/questionnaires/${token}/leave`);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasStartedScreening, exitPopupId, token]);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const updateScores = useCallback((newScores: Record<string, any>) => {
    scoresRef.current = newScores;
    setScores(newScores);
  }, []);

  const isSectionVisible = useCallback((section: FormSection | any, currentScores: Record<string, any>): boolean => {
    // Lead-existence gate, independent of the score gate: a section can require a
    // lead ('exists') or require none ('not_exists'). Both gates must pass.
    if (section.showBasedOnLead) {
      const wantsLead = section.leadCondition !== 'not_exists';
      if (wantsLead !== hasLead) return false;
    }
    if (!section.showBasedOnScore) return true;
    if (!section.scoreNameToCheck || !section.scoreValueToMatch) return true;
    const scoreData = currentScores[section.scoreNameToCheck];
    if (!scoreData) return false;
    return scoreData.score === section.scoreValueToMatch;
  }, [hasLead]);

  const triggerExitPopup = useCallback((): boolean => {
    if (exitPopupId && hasStartedScreening) {
      setShowExitPopup(true);
      return true;
    }
    return false;
  }, [exitPopupId, hasStartedScreening]);

  const getExitPopupContent = useCallback((): { line1: string; line2: string } => {
    const noun = exitPopupVariant === 'questionnaire' ? 'questionnaire' : 'screening';
    if (sectionIndex > 1) {
      return {
        line1: `You haven't finished the ${noun} yet.`,
        line2: 'If you leave now, your progress will be saved, and you can come back later to complete it.',
      };
    }
    return {
      line1: `You haven't started the ${noun} questions yet.`,
      line2: 'If you leave now, you can come back later and start again.',
    };
  }, [sectionIndex, exitPopupVariant]);

  const handleOpenPopup = (popupId: string) => {
    setOpenPopupId(popupId);
  };

  const handleClosePopup = () => {
    if (buttonGroupPopupContext?.resetOnBack) {
      setValue(buttonGroupPopupContext.fieldName, '');
    }
    setOpenPopupId(null);
    setButtonGroupPopupContext(null);
  };

  const scrollToFirstError = () => {
    const firstErrorKey = Object.keys(formState.errors)[0];
    if (!firstErrorKey) return;
    const fieldContainer = document.querySelector(`[data-field-name="${firstErrorKey}"]`);
    if (fieldContainer) {
      fieldContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      console.warn('[ScrollToError] Field container not found for:', firstErrorKey);
    }
  };

  const loadPaymentAmountIfNeeded = async (section: any) => {
    if (!token) return;
    const hasPaymentField = section?.fields?.some((f: any) => f.type === 'stripe_cc');
    if (!hasPaymentField) return;
    try {
      const paymentIntent = await paymentService.createIntent(token);
      setPaymentAmount(paymentIntent.amount);
    } catch (err) {
      console.error('Error loading payment amount:', err);
    }
  };

  const navigateToNextVisibleSection = useCallback(async (updatedScores?: Record<string, any>) => {
    const scoresToUse = updatedScores ?? scores;
    if (sectionIndex < totalSections - 1) {
      let nextIndex = sectionIndex + 1;
      while (nextIndex < totalSections) {
        if (isSectionVisible(sections[nextIndex], scoresToUse)) {
          await loadPaymentAmountIfNeeded(sections[nextIndex]);
          setSectionIndex(nextIndex);
          return;
        }
        nextIndex++;
      }
      console.log('No more visible sections');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIndex, totalSections, sections, scores, isSectionVisible, setSectionIndex]);

  const navigateToPrevVisibleSection = useCallback(() => {
    if (isMobile && sectionPageIndex > 0) {
      autoAdvanceTriggeredRef.current = true;
      setCameFromBackNav(true);
      setSectionPageIndex(prev => prev - 1);
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (sectionIndex > 0) {
      let prevIndex = sectionIndex - 1;
      while (prevIndex >= 0) {
        if (isSectionVisible(sections[prevIndex], scoresRef.current)) {
          if (isMobile) {
            const prevSection = sections[prevIndex];
            const questionFields = prevSection.fields.filter((f: any) =>
              f.type !== 'button' && f.type !== 'content' && f.type !== 'info-banner'
            );
            const totalPages = Math.ceil(questionFields.length / 6);
            const lastPageIndex = Math.max(0, totalPages - 1);
            targetPageIndexRef.current = lastPageIndex;
          }
          setSectionIndex(prevIndex);
          return;
        }
        prevIndex--;
      }
      console.log('No previous visible section');
    }
  }, [sectionIndex, isMobile, sectionPageIndex, sections, isSectionVisible, setSectionIndex]);

  const getFieldsForCurrentPage = useCallback(() => {
    if (!currentSection) return [];
    if (!isMobile) return currentSection.fields;

    const questionFields = currentSection.fields.filter((f: any) =>
      f.type !== 'button' && f.type !== 'content' && f.type !== 'info-banner'
    );

    if (questionFields.length <= 6) return currentSection.fields;

    const start = sectionPageIndex * 6;
    const end = start + 6;
    const paginatedQuestions = questionFields.slice(start, end);
    const buttonFields = currentSection.fields.filter((f: any) => f.type === 'button');

    return [...paginatedQuestions, ...buttonFields];
  }, [currentSection, isMobile, sectionPageIndex]);

  const hasMorePages = useCallback((): boolean => {
    if (!isMobile) return false;
    const questionFields = currentSection?.fields.filter((f: any) =>
      f.type !== 'button' && f.type !== 'content' && f.type !== 'info-banner'
    ) || [];
    return questionFields.length > 6 && (sectionPageIndex + 1) * 6 < questionFields.length;
  }, [isMobile, currentSection, sectionPageIndex]);

  const getPagePopupId = useCallback((): string | null => {
    if (!currentSection) return null;
    const continueButton = currentSection.fields.find((f: any) =>
      f.type === 'button' &&
      (f.action === 'next' || f.action === 'next-and-score' || f.action === 'open-popup' || !f.action)
    );
    return continueButton?.popupId || null;
  }, [currentSection]);

  const findFirstProblematicField = useCallback((section: any): string | null => {
    if (!section) return null;
    for (const field of section.fields) {
      if (field.type === 'button' || field.type === 'content' || field.type === 'info-banner') {
        continue;
      }
      if (field.required) {
        const value = formValues[field.name];
        const isEmpty = value === undefined || value === null || value === '' ||
                       (Array.isArray(value) && value.length === 0) ||
                       (typeof value === 'boolean' && value === false && field.type === 'checkbox');
        if (isEmpty) {
          return field.name;
        }
      }
      if (formState.errors[field.name]) {
        return field.name;
      }
    }
    return null;
  }, [formValues, formState.errors]);

  const injectCustomIcons = (htmlContent: string, customIcons: string[]): string => {
    if (!customIcons || customIcons.length === 0) {
      return htmlContent;
    }
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const listItems = tempDiv.querySelectorAll('li');

    listItems.forEach((li, index) => {
      const iconName = customIcons[index];
      if (!iconName) return;
      const iconSvg = getCustomIcon(iconName);
      if (!iconSvg) return;

      const titleElement = li.querySelector('strong, b');
      let titleText = '';
      let helperText = '';

      if (titleElement) {
        titleText = titleElement.textContent || '';
        const liClone = li.cloneNode(true) as HTMLElement;
        const boldClone = liClone.querySelector('strong, b');
        if (boldClone) boldClone.remove();
        liClone.querySelectorAll('br').forEach(br => br.remove());
        const pTag = liClone.querySelector('p');
        helperText = pTag ? (pTag.textContent?.trim() || '') : (liClone.textContent?.trim() || '');
      } else {
        titleText = li.textContent?.trim() || '';
      }

      li.innerHTML = '';
      li.style.marginBottom = '16px';
      li.style.listStyle = 'none';

      const wrapper = document.createElement('div');
      wrapper.style.display = 'flex';
      wrapper.style.alignItems = 'center';
      wrapper.style.gap = '12px';

      const iconCol = document.createElement('div');
      iconCol.style.flexShrink = '0';
      iconCol.style.display = 'flex';
      iconCol.style.alignItems = 'center';
      iconCol.innerHTML = iconSvg;

      const contentCol = document.createElement('div');
      contentCol.style.flex = '1';
      contentCol.style.display = 'flex';
      contentCol.style.flexDirection = 'column';

      if (titleText) {
        const titleDiv = document.createElement('div');
        titleDiv.style.fontWeight = 'bold';
        titleDiv.style.color = '#3A7380';
        titleDiv.style.fontSize = '1em';
        titleDiv.style.lineHeight = '1.3';
        titleDiv.style.display = 'block';
        titleDiv.textContent = titleText;
        contentCol.appendChild(titleDiv);
      }

      if (helperText) {
        const helperDiv = document.createElement('div');
        helperDiv.style.fontSize = '0.9em';
        helperDiv.style.color = '#5A7380';
        helperDiv.style.lineHeight = '1.5';
        helperDiv.style.marginTop = '4px';
        helperDiv.style.display = 'block';
        helperDiv.textContent = helperText;
        contentCol.appendChild(helperDiv);
      }

      wrapper.appendChild(iconCol);
      wrapper.appendChild(contentCol);
      li.appendChild(wrapper);
    });

    return tempDiv.innerHTML;
  };

  // ─── Smart scroll effect ──────────────────────────────────────────────────
  useEffect(() => {
    if (!scrollContainerRef.current || !currentSection) return;

    const isForwardNavigation = sectionIndex > previousSectionRef.current;

    if (isForwardNavigation) {
      const problematicField = findFirstProblematicField(currentSection);
      if (problematicField) {
        const fieldContainer = document.querySelector(`[data-field-name="${problematicField}"]`);
        if (fieldContainer) {
          fieldContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }

    previousSectionRef.current = sectionIndex;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIndex]);

  // ─── Section visibility effect ───────────────────────────────────────────
  useEffect(() => {
    if (!sections || sections.length === 0 || !currentSection) return;

    if (!isSectionVisible(currentSection, scores)) {
      let nextIndex = sectionIndex + 1;
      while (nextIndex < totalSections) {
        if (isSectionVisible(sections[nextIndex], scores)) {
          setSectionIndex(nextIndex);
          return;
        }
        nextIndex++;
      }

      let prevIndex = sectionIndex - 1;
      while (prevIndex >= 0) {
        if (isSectionVisible(sections[prevIndex], scores)) {
          setSectionIndex(prevIndex);
          return;
        }
        prevIndex--;
      }

      console.warn('Current section is hidden and no visible sections found');
    }
  }, [scores, sections, currentSection, sectionIndex, totalSections, isSectionVisible, setSectionIndex]);

  // ─── Initialize previousFilledCountRef on section/page change ─────────────
  useEffect(() => {
    if (!isMobile || !currentSection) return;

    const questionFields = currentSection.fields.filter((f: any) =>
      f.type !== 'button' && f.type !== 'content' && f.type !== 'info-banner'
    );
    const start = sectionPageIndex * 6;
    const end = start + 6;
    const currentPageFields = questionFields.slice(start, end);

    const filledCount = currentPageFields.filter((field: any) => {
      const value = formValues[field.name];
      return value !== undefined && value !== null && value !== '' &&
             !(Array.isArray(value) && value.length === 0);
    }).length;

    previousFilledCountRef.current = filledCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIndex, sectionPageIndex, currentSection, isMobile]);

  // ─── Auto-advance logic (mobile only) ─────────────────────────────────────
  useEffect(() => {
    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }

    if (!isMobile || !currentSection || autoAdvanceTriggeredRef.current) {
      return;
    }

    const questionFields = currentSection.fields.filter((f: any) =>
      f.type !== 'button' && f.type !== 'content' && f.type !== 'info-banner'
    );

    const questionCount = questionFields.length;
    const start = sectionPageIndex * 6;
    const end = start + 6;
    const currentPageFields = questionFields.slice(start, end);

    const allFilled = currentPageFields.every((field: any) => {
      if (!field.required) return true;
      const value = formValues[field.name];
      if (value === undefined || value === null || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (typeof value === 'boolean' && value === false && field.type === 'checkbox') return false;
      if (!validateFieldValue(field, value, formValues)) return false;
      return true;
    });

    if (!allFilled) return;

    const filledCount = currentPageFields.filter((field: any) => {
      const value = formValues[field.name];
      return value !== undefined && value !== null && value !== '' &&
             !(Array.isArray(value) && value.length === 0);
    }).length;

    if (filledCount <= previousFilledCountRef.current) {
      previousFilledCountRef.current = filledCount;
      return;
    }

    previousFilledCountRef.current = filledCount;

    const hasMore = (sectionPageIndex + 1) * 6 < questionFields.length;
    const isLastPage = !hasMore;
    const shouldAutoAdvance = questionCount === 6 || (questionCount > 6 && !isLastPage);

    if (!shouldAutoAdvance) return;

    autoAdvanceTriggeredRef.current = true;

    autoAdvanceTimeoutRef.current = setTimeout(() => {
      if (hasMore) {
        const popupId = getPagePopupId();
        if (popupId) {
          setNavigateToPage(sectionPageIndex + 1);
          setOpenPopupId(popupId);
        } else {
          setSectionPageIndex(sectionPageIndex + 1);
          autoAdvanceTriggeredRef.current = false;
        }
      } else {
        // Last page or single-page section - call caller's save function
        const popupId = getPagePopupId();
        if (popupId && onAutoAdvanceSection) {
          // Save section first, then show popup
          onAutoAdvanceSection(true).then((success) => {
            if (success) {
              setNavigateAfterPopup(true);
              setOpenPopupId(popupId);
            }
          });
        } else if (onAutoAdvanceSection) {
          // No popup: direct navigation
          onAutoAdvanceSection(false);
        }
      }
    }, 300);

    return () => {
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
        autoAdvanceTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, currentSection, formValues, sectionPageIndex]);

  return {
    // form
    control,
    trigger,
    watch,
    setValue,
    reset,
    getValues,
    formState,
    formValues,

    // state
    saving,
    setSaving,
    openPopupId,
    setOpenPopupId,
    showExitPopup,
    setShowExitPopup,
    hasFilledFields,
    setHasFilledFields,
    paymentFormReady,
    setPaymentFormReady,
    paymentAmount,
    setPaymentAmount,
    sectionPageIndex,
    setSectionPageIndex,
    scores,
    setScores,
    cameFromBackNav,
    setCameFromBackNav,
    navigateAfterPopup,
    setNavigateAfterPopup,
    buttonGroupPopupContext,
    setButtonGroupPopupContext,
    navigateToPage,
    setNavigateToPage,

    // refs
    scoresRef,
    scrollContainerRef,
    stripeCCFieldRef,
    autoAdvanceTriggeredRef,
    targetPageIndexRef,
    skipBeforeUnloadRef,

    // computed
    isMobile,
    currentSection,

    // handlers
    updateScores,
    isSectionVisible,
    triggerExitPopup,
    getExitPopupContent,
    handleOpenPopup,
    handleClosePopup,
    scrollToFirstError,
    loadPaymentAmountIfNeeded,
    navigateToNextVisibleSection,
    navigateToPrevVisibleSection,
    getFieldsForCurrentPage,
    hasMorePages,
    getPagePopupId,
    findFirstProblematicField,
    injectCustomIcons,
  };
}
